import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert, Dropdown,
  OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listMyAssignments } from '../../services/toolAssignmentService';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import ToolReturnModal from './components/ToolReturnModal';
import SubAssignModal from './components/SubAssignModal';
import ToolEmptyState from './components/ToolEmptyState';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ToolList.css';

const CONDITION_LABELS = { good: 'Buena', damaged: 'Dañada', lost: 'Perdida', maintenance: 'Mantenimiento' };
const CONDITION_COLORS = { good: 'success', damaged: 'warning', lost: 'danger', maintenance: 'info' };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-CR') : '—';
const daysOut = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

const MyToolsPage = () => {
  const { user, isTechnician, isAdmin } = useAuth();
  const { viewMode, setViewMode, isCardView } = useViewToggle();

  const [returnState, setReturnState] = useState(null);
  const [subAssign, setSubAssign] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-assignments', user?.id],
    queryFn: () => listMyAssignments({ status: 'active' }),
    enabled: !!user?.id,
    staleTime: 30 * 1000
  });

  if (!isTechnician && !isAdmin) {
    return <Alert variant="danger" className="m-3">Esta página es para técnicos.</Alert>;
  }

  const assignments = data?.data || [];

  const renderCard = (a) => {
    const isPrimary = !a.parent_assignment_id;
    return (
      <Col key={a.id} xs={12} sm={6} lg={4} className="mb-3">
        <Card className="h-100 modern-card tool-card">
          <div className="tool-card-photo-wrap">
            {a.tool?.image_url ? (
              <img src={a.tool.image_url} alt={a.tool.name} className="tool-card-photo" />
            ) : (
              <div className="tool-card-photo-placeholder">
                <i className="bi bi-tools" />
              </div>
            )}
            <div className="position-absolute top-0 end-0 p-2">
              {!isPrimary && (
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip>
                      Te la dio otro técnico. Cuando la devuelvas, vuelve a él (no a bodega).
                    </Tooltip>
                  }
                >
                  <Badge bg="info" style={{ cursor: 'help' }}>
                    Sub-asignada <i className="bi bi-info-circle ms-1" />
                  </Badge>
                </OverlayTrigger>
              )}
            </div>
          </div>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-1">
              <Card.Title className="mb-0 fs-6 fw-bold text-truncate" title={a.tool?.name}>
                {a.tool?.name}
              </Card.Title>
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link" className="p-0 text-muted"
                  style={{ boxShadow: 'none' }}
                  aria-label={`Acciones de ${a.tool?.name || 'la herramienta'}`}
                >
                  <i className="bi bi-three-dots-vertical" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setReturnState({ assignment: a, mode: 'return' })}>
                    <i className="bi bi-box-arrow-in-left me-2 text-success" />Devolver
                  </Dropdown.Item>
                  {isPrimary && (
                    <Dropdown.Item onClick={() => setSubAssign(a)}>
                      <i className="bi bi-share me-2 text-primary" />Sub-asignar
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  {/* Marcar perdida escribe en `tools`, y `tools_update` es
                      `is_admin()` en la base: para un técnico esto SIEMPRE
                      terminaba en un error crudo de PostgREST. Se muestra
                      deshabilitado —"no me toca" se distingue de "está
                      roto"— en vez de ofrecer una acción condenada. */}
                  {isAdmin ? (
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => setReturnState({ assignment: a, mode: 'lost' })}
                    >
                      <i className="bi bi-exclamation-triangle me-2" />Reportar perdida
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item disabled title="Solo un administrador puede marcar una herramienta como perdida. Avisale y devolvé el resto.">
                      <i className="bi bi-exclamation-triangle me-2" />Reportar perdida (lo hace un admin)
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <small className="text-muted font-monospace d-block">{a.tool?.serial_number}</small>
            <div className="d-flex flex-wrap gap-1 mt-2">
              {a.tool?._categoryLabel && <Badge bg="light" text="dark">{a.tool.category}</Badge>}
              {a.tool?.condition && (
                <Badge bg={CONDITION_COLORS[a.tool.condition]}>
                  {CONDITION_LABELS[a.tool.condition]}
                </Badge>
              )}
            </div>
            {a.project && (
              <div className="mt-2 small text-muted">
                <i className="bi bi-briefcase me-1" />{a.project.title || a.project.project_number}
              </div>
            )}
            <div className="mt-1 small text-muted">
              <i className="bi bi-calendar me-1" />
              {fmtDate(a.assigned_at)}
              {(() => {
                const d = daysOut(a.assigned_at);
                return d > 0 ? (
                  <span className={`ms-1 ${d > 30 ? 'text-warning fw-bold' : ''}`}>({d}d)</span>
                ) : null;
              })()}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderRow = (a) => {
    const isPrimary = !a.parent_assignment_id;
    return (
      <tr key={a.id}>
        <td>
          {a.tool?.image_url ? (
            <img src={a.tool.image_url} alt="" className="tool-row-thumb" />
          ) : (
            <div className="tool-row-thumb-placeholder"><i className="bi bi-tools" /></div>
          )}
        </td>
        <td>
          <strong>{a.tool?.name}</strong>
          {!isPrimary && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  Te la dio otro técnico. Cuando la devuelvas, vuelve a él (no a bodega).
                </Tooltip>
              }
            >
              <Badge bg="info" className="ms-2" style={{ cursor: 'help' }}>sub</Badge>
            </OverlayTrigger>
          )}<br />
          <small className="text-muted font-monospace">{a.tool?.serial_number}</small>
        </td>
        <td>{a.tool?._categoryLabel || '—'}</td>
        <td>{a.project?.title || a.project?.project_number || <span className="text-muted">—</span>}</td>
        <td>
          {fmtDate(a.assigned_at)}
          {(() => {
            const d = daysOut(a.assigned_at);
            return d > 0 ? (
              <small className={`ms-2 ${d > 30 ? 'text-warning fw-bold' : 'text-muted'}`}>{d}d</small>
            ) : null;
          })()}
        </td>
        <td>
          {a.tool?.condition && (
            <Badge bg={CONDITION_COLORS[a.tool.condition]}>
              {CONDITION_LABELS[a.tool.condition]}
            </Badge>
          )}
        </td>
        <td>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link" className="p-0 text-muted"
              style={{ boxShadow: 'none' }}
              aria-label={`Acciones de ${a.tool?.name || 'la herramienta'}`}
            >
              <i className="bi bi-three-dots-vertical" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setReturnState({ assignment: a, mode: 'return' })}>
                <i className="bi bi-box-arrow-in-left me-2 text-success" />Devolver
              </Dropdown.Item>
              {isPrimary && (
                <Dropdown.Item onClick={() => setSubAssign(a)}>
                  <i className="bi bi-share me-2 text-primary" />Sub-asignar
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              {/* Ver la nota de la vista de tarjetas: `tools_update` es admin-only. */}
              {isAdmin ? (
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => setReturnState({ assignment: a, mode: 'lost' })}
                >
                  <i className="bi bi-exclamation-triangle me-2" />Reportar perdida
                </Dropdown.Item>
              ) : (
                <Dropdown.Item disabled title="Solo un administrador puede marcar una herramienta como perdida. Avisale y devolvé el resto.">
                  <i className="bi bi-exclamation-triangle me-2" />Reportar perdida (lo hace un admin)
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </tr>
    );
  };

  return (
    <DashboardLayout>
    <Container fluid className="py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 className="modern-title mb-0">
          <i className="bi bi-tools me-2" />Mis herramientas
          {assignments.length > 0 && (
            <Badge bg="primary" className="ms-2 fs-6">{assignments.length}</Badge>
          )}
        </h2>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button as={Link} to="/herramientas/mis-solicitudes" variant="outline-secondary">
            <i className="bi bi-clipboard-check me-1" />Mis solicitudes
          </Button>
          <Button as={Link} to="/bodega" variant="outline-primary">
            <i className="bi bi-shop me-1" />Ver bodega
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      )}
      {error && <Alert variant="danger">Error al cargar: {error.message}</Alert>}

      {!isLoading && assignments.length === 0 && (
        <ToolEmptyState variant="tech_no_assignments" />
      )}

      {!isLoading && assignments.length > 0 && (
        isCardView ? (
          <Row>{assignments.map(renderCard)}</Row>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Foto</th>
                  <th>Herramienta</th>
                  <th>Categoría</th>
                  <th>Proyecto</th>
                  <th>Asignada</th>
                  <th>Condición</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>{assignments.map(renderRow)}</tbody>
            </table>
          </div>
        )
      )}

      <ToolReturnModal
        show={!!returnState}
        onHide={() => setReturnState(null)}
        assignment={returnState?.assignment}
        mode={returnState?.mode}
      />
      <SubAssignModal
        show={!!subAssign}
        onHide={() => setSubAssign(null)}
        assignment={subAssign}
      />
    </Container>
    </DashboardLayout>
  );
};

export default MyToolsPage;
